/**
 * Utility library for working with dates.
 * Used here to provide default date values when none are supplied.
 */
import dayjs from "dayjs";

/**
 * Builds the payload object used to create or check a segment on the backend.
 *
 * The function takes the current state of the segment form:
 * - metadata (country, brand, folder, segmentName, etc.),
 * - inclusion and exclusion sections with conditions,
 * - operation type (create | check | ...),
 * and converts it into the exact JSON structure expected by the API.
 *
 * @param country       Country identifier for the segment.
 * @param segmentName   Name of the segment defined by the user.
 * @param sections      Inclusion sections from the form state.
 * @param exclusions    Exclusion sections from the form state.
 * @param type          Operation type (e.g. "create", "edit", "check").
 * @param user          User identifier or object performing the operation.
 * @param brand         Brand associated with the segment.
 * @param folder        Folder or grouping information for the segment.
 * @param segmentId     Existing segment identifier (for edits) or null for creates.
 * @param pSegmentName  Previous segment name (used when renaming).
 * @param tag           Optional tag to be attached to the segment (ignored for "check").
 * @returns             Payload object ready to be sent to the segment API.
 *
 * @example
 * const payload = formatCreateSegmentPayload({
 *   country,
 *   segmentName,
 *   sections,
 *   exclusions,
 *   type: "create",
 *   user,
 *   brand,
 *   folder,
 *   segmentId,
 *   pSegmentName,
 *   tag
 * });
 */
const formatCreateSegmentPayload = ({
  country,
  segmentName = "",
  sections,
  exclusions,
  type = "create",
  user,
  brand,
  folder,
  segmentId,
  pSegmentName,
  tag = ""
}: any): any => {
  /**
   * Maps the internal form representation of sections into the payload format.
   *
   * Each section contains an array of conditions. On the form side:
   * - conditions hold auxiliary fields such as type, logic, lower/upper bounds, etc.
   * - the API expects a normalized structure with:
   *   - operator: comparison operator for each condition,
   *   - value:    normalized value (single value, array, or range),
   *   - field:    field identifier,
   *   - table:    table identifier where the field belongs.
   *
   * This helper extracts and normalizes those values for every condition
   * in every section.
   *
   * @param sections Sections array from the form state (include or exclude).
   * @returns        Sections array formatted for the backend payload.
   */
  const mapSections = (sections: any[]) =>
    sections.map((section: any) => {
      // Safely extract conditions from the section, defaulting to an empty array.
      const { conditions = [] } = section;

      return {
        // Operator that connects conditions inside a section (AND/OR).
        // Fallbacks to "AND" when no explicit logic is provided.
        operator: conditions?.[1]?.logic || "AND",
        // Map each condition from the form into the normalized payload shape.
        conditions: conditions.map((condition: any) => {
          // Extract the raw condition properties from the form.
          const { table, field = "", value, operator, type } = condition;

          // tempVal holds the final value that will be sent to the backend.
          let tempVal;
          // tempOperator holds the final comparison operator sent to the backend.
          let tempOperator;

          // Handle number type conditions
          // Examples: equals, greater than, between, etc.
          if (type === "number") {
            // Default numeric operator is equality.
            tempOperator = operator || "=";

            // Range-based numeric operators require an array of two values.
            if (operator === "number between" || operator === "number not between") {
              if (Array.isArray(value)) {
                // If the form already provides an array, use it directly.
                tempVal = [...value];
              } else if (condition.lower !== undefined && condition.upper !== undefined) {
                // When lower and upper bounds are stored separately, combine them.
                tempVal = [condition.lower, condition.upper];
              } else {
                // Fallback: provide an empty range.
                tempVal = ["", ""];
              }
            } else {
              // Single numeric value case.
              // When a value is provided, cast it to a number; otherwise default to 0.
              tempVal = value !== undefined && value !== null ? Number(value) : 0;
            }
          }
          // Handle date type conditions
          // Examples: on, before, after, between, etc.
          else if (type === "date") {
            // Default date operator is "on".
            tempOperator = operator ? operator : "on";
            // If no date is provided, default to today's date in YYYY-MM-DD format.
            tempVal = value ? value : dayjs().format("YYYY-MM-DD");
          }
          // Handle text type conditions
          // Text can represent single or multi-line input that may be sent as an array.
          else if (type === "text") {
            // Default text operator is "contains".
            tempOperator = operator || "contains";

            if (typeof value === "string") {
              // Split multiline text into an array of trimmed, non-empty lines.
              tempVal = value
                .split("\n")
                .map((v: string) => v.trim())
                .filter((v: string) => v !== "");
            } else if (Array.isArray(value)) {
              // If the form already stores an array, use it directly.
              tempVal = value;
            } else {
              // Fallback: empty array of values.
              tempVal = [];
            }
          }
          // Handle all other condition types (e.g. select, boolean, etc.)
          else {
            // For simple types, forward the raw value or default to an empty string.
            tempVal = value || "";
            // Default operator for non-specialized types is equality.
            tempOperator = operator || "=";
          }

          // Return the normalized condition object exactly as the API expects.
          return { table, field, operator: tempOperator, value: tempVal };
        }),
      };
    });

  /**
   * Compose the final payload object that will be sent to the API.
   * It includes segment metadata as well as normalized include/exclude conditions.
   */
  const payload: any = {
    // Segment metadata and identification.
    segmentName,
    country,
    brand,
    folder,
    type,
    user,
    segmentId: segmentId,
    pSegmentName,
    // Include tag only for non-"check" operations. For "check" it is ignored.
    ...(type !== "check" && { tag }),
    // Inclusion and exclusion condition trees.
    conditions: {
      include: {
        // Global operator combining inclusion sections (AND/OR).
        // When missing, default to "AND".
        operator: sections?.[1]?.operator || "AND",
        // Sections transformed into backend-ready format.
        sections: mapSections(sections),
      },
      exclude: {
        // Global operator combining exclusion sections (AND/OR).
        // When missing, default to "AND".
        operator: exclusions?.[1]?.operator || "AND",
        // Exclusion sections transformed into backend-ready format.
        sections: mapSections(exclusions),
      },
    },
  };

  // Return the fully constructed payload object.
  return payload;
};

export default formatCreateSegmentPayload;